import React from "react";
import { graphql } from "gatsby";
import { FeaturedMedia } from "./FeaturedMedia";
import {
  KontentComponentLayout,
  KontentComponentSettings,
  KontentLinkedImage,
  KontentLinkedVectorArt,
  KontentLinks,
  KontentMultiChoice,
  KontentRichText,
  KontentVideo,
  StringValue,
  YesNo,
  LeftRight,
} from "../../../types";
import { formatImages, formatVector } from "../../../lib/images";
import { getMultiChoice } from "../../../lib/multichoice";
import { getComponentSettings } from "../../../lib/componentSettings";
import { formatLinks } from "../../../lib/links";
import { formatVideo } from "../../../lib/videos";

export type TopBottom = "top" | "bottom";
export type ImageFitOptions = "contain" | "cover";

interface Props extends KontentComponentSettings, KontentComponentLayout {
  accent_bottom_right_corner: KontentMultiChoice<YesNo>;
  corner_rounding: KontentMultiChoice<TopBottom>;
  featured_media: KontentLinkedImage;
  featured_media_placement: KontentMultiChoice<LeftRight>;
  heading: StringValue;
  link_configuration__link: KontentLinks;
  text: KontentRichText;
  vector: KontentLinkedVectorArt;
  vector_placement: KontentMultiChoice<TopBottom>;
  image_fit: KontentMultiChoice<ImageFitOptions>;
}

const FeaturedMediaContainer: React.FC<Props> = ({
  accent_bottom_right_corner,
  corner_rounding,
  featured_media,
  featured_media_placement,
  heading,
  image_fit,
  link_configuration__link,
  text,
  vector,
  vector_placement,
  ...props
}) => {
  const componentSettings = getComponentSettings({ ...props });
  const featuredMedia =
    featured_media.value[0]?.system.type === "video"
      ? formatVideo(
          featured_media?.value[0]
            .elements as unknown as KontentVideo["elements"]
        )
      : formatImages(featured_media?.value[0]?.elements).largeImage;
  const accentBottomRightCorner =
    getMultiChoice(accent_bottom_right_corner) === "yes" ? true : false;
  const cornerRounding = getMultiChoice(corner_rounding);
  const featuredMediaPlacement = getMultiChoice(featured_media_placement);
  const vectorPlacement = getMultiChoice(vector_placement);
  const links = formatLinks(link_configuration__link.modular_content);
  const vectorArt = formatVector(vector?.value[0]?.elements);
  const imageFit = getMultiChoice(image_fit);
  return (
    <FeaturedMedia
      accentBottomRightCorner={accentBottomRightCorner}
      cornerRounding={cornerRounding}
      featuredMedia={featuredMedia}
      featuredMediaPlacement={featuredMediaPlacement}
      heading={heading.value}
      cta={links[0]}
      secondaryCta={links[1]}
      text={text}
      vector={vectorArt}
      vectorPlacement={vectorPlacement}
      imageFit={imageFit}
      {...componentSettings}
    ></FeaturedMedia>
  );
};

export default FeaturedMediaContainer;

export const fragmentFeaturedMediaContainer = graphql`
  fragment FeaturedMedia on kontent_item_featured_media {
    elements {
      accent_bottom_right_corner {
        value {
          codename
        }
      }
      component_layout__component_width {
        value {
          codename
        }
      }
      component_settings__anchor_name {
        value
      }
      component_settings__hide_on_mobile {
        value {
          codename
        }
      }
      corner_rounding {
        value {
          codename
        }
      }
      featured_media {
        value {
          ...kontentImage
          ...kontentVideo
        }
      }
      featured_media_placement {
        value {
          codename
        }
      }
      image_fit {
        value {
          codename
        }
      }
      heading {
        value
      }
      link_configuration__link {
        modular_content {
          ...kontentRichTextLink
        }
      }
      text {
        ...kontentSimpleRichText
      }
      vector {
        value {
          ...kontentVectorArt
        }
      }
      vector_placement {
        value {
          codename
        }
      }
    }
  }
`;
